import React, {
  useCallback,
  useMemo,
  useRef,
  useState,
  useEffect,
} from "react";
// AG Grid styles and modules removed as we are switching to MUI DataGrid Premium
import { defaultConfigItem } from "../../../constants";

// Google Places removed - loader import kept if needed elsewhere
// import GooglePlacesEditor from "../../editors/address/GooglePlacesEditor";
import GoogleAutocompleteEditCell from "../../editors/address/GoogleAutoCompleteEditCell";
import MultiSelectEditor from "../../editors/multiSelect/MultiSelectEditor";
import MultiSelectDisplayRenderer from "../../editors/renderers/MultiSelectDisplayRenderer";
import LegacyValueTrackRenderer from "../../editors/renderers/LegacyValueTrackRenderer";
import getSeedLocations from "../../data/getSeedLocations";
import NexusDataGrid from "../../core/NexusDataGrid";
import NexusMappingEditor from "../../core/NexusMappingEditor";
import "../../styles/styles.css";
import MergeValueEditCell from "../../editors/MergeValueEditCell";
import {
  jdeF4100Fields,
  buildInitialMappingsFromSuggestions,
  buildSelectForF4100,
} from "../../../mappings/jdeF4100LocationMapping";

// License key is set centrally in App.js

// Stable id generator for rows
const genId = () =>
  `loc_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;

// Utility to clone a default row
const createNewRow = () => ({
  ...defaultConfigItem,
  __id: genId(),
  __status: "new",
  // New schema fields
  locationName: "",
  locationId: "",
  locationUsage: [],
  locationType: [],
  languageId: "",
  timeZoneId: "",
  countryId: "",
  address: "",
  commUsage: [],
  regionId: "",
  regionName: "",
});

const Locations = ({ questionnaireData, setQuestionnaireData }) => {
  const gridRef = useRef(null);
  // Holds the auto-generated or user-modified SQL to show in Query Editor
  const [sqlQuery, setSqlQuery] = useState("");
  // Holds current column mappings (targetId -> { mappingType, sourceId })
  const [columnMappings, setColumnMappings] = useState(
    buildInitialMappingsFromSuggestions()
  );
  const incomingLocations = questionnaireData?.foundationalSetup?.locations;

  // Search Location box and Google Places autocomplete removed per request
  const initial = useMemo(() => {
    const arr =
      incomingLocations && incomingLocations.length > 0
        ? incomingLocations
        : getSeedLocations();
    return arr.map((r) => ({ ...r, __id: r.__id || genId() }));
  }, [incomingLocations]);

  // Start with incoming or empty; do not auto-fetch until user applies mapping
  const [rowData, setRowData] = useState(
    incomingLocations && incomingLocations.length > 0 ? initial : []
  );
  // Track edited locationId values per row: { [__id]: { original, current } }
  const [locIdChanges, setLocIdChanges] = useState({});
  const [isMappingOpen, setIsMappingOpen] = useState(false);
  const [searchText] = useState("");

  // Avoid syncing rowData from parent on every update to prevent update loops
  // Parent already holds a copy via setQuestionnaireData effect below.

  // Removed legacy AG Grid loader; data loads after mapping apply via /api/db/query

  // Do NOT auto-load from backend; user will apply mapping to load
  useEffect(() => {
    // Build an initial SQL preview from our default suggestions so the Query Editor shows something
    const sql = buildSelectForF4100(columnMappings);
    setSqlQuery(sql);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // Persist to parent whenever rowData changes, but avoid redundant updates
  useEffect(() => {
    setQuestionnaireData((prev) => {
      const prevLoc = prev?.foundationalSetup?.locations;
      if (prevLoc === rowData) return prev; // no change needed
      return {
        ...prev,
        foundationalSetup: {
          ...prev.foundationalSetup,
          locations: rowData,
        },
      };
    });
  }, [rowData, setQuestionnaireData]);

  // Region options no longer loaded; display DB-provided regionName directly

  const filteredRowData = useMemo(() => {
    const rows = Array.isArray(rowData) ? rowData : [];
    const q = (searchText || "").toLowerCase().trim();
    if (!q) return rows;
    return rows.filter((r) => {
      const fields = [
        r.locationName,
        r.locationId,
        r.languageId,
        r.timeZoneId,
        r.countryId,
        r.regionName,
        r.subregion,
        r.address,
        Array.isArray(r.locationUsage) ? r.locationUsage.join(",") : "",
        Array.isArray(r.locationType) ? r.locationType.join(",") : "",
        Array.isArray(r.commUsage) ? r.commUsage.join(",") : "",
      ];
      return fields.some((v) =>
        String(v || "")
          .toLowerCase()
          .includes(q)
      );
    });
  }, [rowData, searchText]);

  // (search field now uses shared GoogleAutoCompleteField)

  const columnDefs = useMemo(
    () => [
      {
        headerName: "Location Name",
        field: "locationName",
        editable: (p) => p.data?.__status !== "merged",
        flex: 1.2,
      },
      {
        headerName: "Location ID",
        field: "locationId",
        editable: true,
        width: 160,
        renderCell: LegacyValueTrackRenderer,
        // Use MUI-native edit component; do not use AG's cellEditor here
        renderEditCell: MergeValueEditCell,
        // Provide a dynamic options provider used by MergeValueEditCell when row is merged
        getMergeOptions: () =>
          Array.from(
            new Set(
              Object.values(locIdChanges)
                .map((v) => v?.current)
                .filter(Boolean)
            )
          ),
      },
      {
        headerName: "Location Usage",
        field: "locationUsage",
        editable: (p) => p.data?.__status !== "merged",
        renderCell: MultiSelectDisplayRenderer,
        cellEditorParams: { options: ["Business Site", "Warehouse"] },
        valueFormatter: (p) =>
          Array.isArray(p.value)
            ? [...p.value].sort((a, b) => a.localeCompare(b)).join(", ")
            : "",
        flex: 1,
      },
      {
        headerName: "Location Type",
        field: "locationType",
        editable: (p) => p.data?.__status !== "merged",
        renderCell: MultiSelectDisplayRenderer,
        renderEditCell: MultiSelectEditor,
        cellEditorParams: { options: ["Home", "Office"] },
        cellEditorPopup: true,
        valueFormatter: (p) =>
          Array.isArray(p.value)
            ? [...p.value].sort((a, b) => a.localeCompare(b)).join(", ")
            : "",
        flex: 1,
      },
      {
        headerName: "Language ID",
        field: "languageId",
        editable: (p) => p.data?.__status !== "merged",
        cellEditorParams: { values: ["en_US", "fr_CA"] },
        width: 140,
      },
      {
        headerName: "Time Zone ID",
        field: "timeZoneId",
        editable: (p) => p.data?.__status !== "merged",
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: ["EST", "PST", "CST"] },
        width: 140,
      },
      {
        headerName: "Country ID",
        field: "countryId",
        editable: (p) => p.data?.__status !== "merged",
        cellEditor: "agSelectCellEditor",
        cellEditorParams: { values: ["USA", "Canada"] },
        width: 140,
      },
      {
        headerName: "Address",
        field: "address",
        renderEditCell: GoogleAutocompleteEditCell,
        editable: (p) => p.data?.__status !== "merged",
        width: 360,
      },
      // Subregion added above Region Name, both read-only from DB
      {
        headerName: "Subregion",
        field: "subregion",
        editable: false,
        width: 200,
      },
      {
        headerName: "Communication Usage Behavior",
        field: "commUsage",
        editable: (p) => p.data?.__status !== "merged",
        cellRenderer: MultiSelectDisplayRenderer,
        cellEditor: MultiSelectEditor,
        cellEditorParams: { options: ["Shipping", "Billing"] },
        cellEditorPopup: true,
        valueFormatter: (p) =>
          Array.isArray(p.value)
            ? [...p.value].sort((a, b) => a.localeCompare(b)).join(", ")
            : "",
        flex: 1.4,
      },
      {
        headerName: "Region Name",
        field: "regionName",
        editable: false,
        flex: 1.2,
        enableRowGroup: true,
        sortable: true,
      },
    ],
    [locIdChanges]
  );

  const defaultColDef = useMemo(
    () => ({
      sortable: true,
      resizable: true,
      filter: true,
      cellClass: "flex items-center",
      minWidth: 150,
      // Enable row grouping capabilities
      enableRowGroup: false,
      rowDrag: false,
      suppressMovable: false,
    }),
    []
  );

  // Removed AG Grid autoGroupColumnDef (not used in MUI DataGrid)

  // Keep color coding via inline row styles to ensure visibility
  // Replace AG getRowStyle with MUI getRowClassName to keep color coding
  const getRowClassName = useCallback((params) => {
    const s = params?.row?.__status;
    if (s === "new") return "bg-green-50";
    if (s === "deleted") return "bg-red-50";
    if (s === "merged") return "bg-amber-50";
    return "";
  }, []);

  // Generic hook for merged rows (no-op in MUI grid for now)
  const onRowMerged = useCallback(() => {}, []);

  // In MUI DataGrid, handle edits via processRowUpdate passed into NexusDataGrid
  const processRowUpdate = useCallback((newRow, oldRow) => {
    const id = newRow?.__id || oldRow?.__id;
    if (id && newRow.locationId !== oldRow.locationId) {
      setLocIdChanges((prev) => {
        const existing = prev[id];
        const original = existing?.original ?? oldRow.locationId ?? "";
        const next = { ...prev };
        if (newRow.locationId === original) {
          delete next[id];
          return next;
        }
        next[id] = { original, current: newRow.locationId };
        return next;
      });
    }
    setRowData((prev) => prev.map((r) => (r.__id === id ? { ...newRow } : r)));
    return newRow;
  }, []);

  const handleAdd = () => {
    setRowData((prev) => [createNewRow(), ...prev]);
  };

  // SQL apply handler from NexusGrid: we only persist the query text for now
  const handleSqlApply = (q) => {
    if (typeof q === "string") setSqlQuery(q);
    // Per requirements, do not fetch/apply data on SQL apply yet
  };

  const refreshRowById = useCallback(() => {}, []);

  // Undo handler: for merged rows, restore original locationId and clear status/change entry
  const onRowUndo = useCallback(
    (rowId, dataRow, status) => {
      if (status !== "merged") return;
      const original = locIdChanges?.[rowId]?.original;
      setRowData((prev) =>
        prev.map((r) =>
          r.__id === rowId
            ? {
                ...r,
                __status: undefined,
                locationId: original ?? r.locationId,
              }
            : r
        )
      );
      setLocIdChanges((prev) => {
        if (!prev || !prev[rowId]) return prev;
        const next = { ...prev };
        delete next[rowId];
        return next;
      });
      setTimeout(() => refreshRowById(rowId), 0);
    },
    [locIdChanges, refreshRowById]
  );

  return (
    <>
      <NexusDataGrid
        key="locations"
        title="Locations"
        actionText="Add Location"
        onAdd={handleAdd}
        onOpenMapping={() => setIsMappingOpen(true)}
        sqlInitialQuery={sqlQuery}
        onSqlApply={handleSqlApply}
        columnDefs={columnDefs}
        defaultColDef={defaultColDef}
        rowData={filteredRowData}
        showRowNumbers={true}
        showActions={true}
        getRowId={(row) => row.__id}
        containerClassName="border border-gray-200 rounded-lg bg-white shadow-sm mb-6"
        gridRef={gridRef}
        rowHeight={36}
        processRowUpdate={processRowUpdate}
        getRowClassName={getRowClassName}
        context={{
          setRowData: setRowData,
          refreshRowById: refreshRowById,
          onRowMerged,
          onRowUndo,
          locIdChanges,
          actionsDisabled: (actionName, row) => {
            if (actionName !== "merge") return undefined;
            const anyEdited = Object.values(locIdChanges).some(
              (v) => !!v?.current
            );
            const rowEdited = row && row.__id && locIdChanges[row.__id];
            // disable if no edited values exist globally OR if this row itself is an edited source
            return !anyEdited || !!rowEdited;
          },
        }}
      />

      {isMappingOpen && (
        <div className="fixed inset-0 z-[999] flex items-center justify-center">
          <div
            className="absolute inset-0 bg-black/40"
            onClick={() => setIsMappingOpen(false)}
          />
          <div className="relative w-[92vw] max-w-6xl h-[80vh] bg-white rounded-lg shadow-xl border border-slate-200 overflow-hidden">
            <div className="flex items-center justify-between px-4 py-2 border-b border-slate-200">
              <div className="text-sm font-medium text-slate-800">
                Locations Mapping
              </div>
              <button
                className="px-2 py-1 rounded border border-slate-300 text-slate-700 hover:bg-slate-50"
                onClick={() => setIsMappingOpen(false)}
              >
                Close
              </button>
            </div>
            <div className="p-2 h-[calc(100%-40px)]">
              <NexusMappingEditor
                sourceColumns={jdeF4100Fields}
                targetColumns={buildTargetsFromColumns(columnDefs)}
                initialMappings={columnMappings}
                onMappingsChange={async (m) => {
                  // Persist mapping
                  setColumnMappings(m);
                  // Build SQL from applied mappings and show in Query Editor
                  const sql = buildSelectForF4100(m);
                  setSqlQuery(sql);
                  // Execute the select to fetch data and map into the grid
                  try {
                    const res = await fetch("/api/db/query", {
                      method: "POST",
                      headers: { "Content-Type": "application/json" },
                      body: JSON.stringify({ sql }),
                    });
                    const payload = await res.json();
                    if (!payload.ok)
                      throw new Error(payload.error || "Query failed");
                    const rows = Array.isArray(payload.rows)
                      ? payload.rows
                      : [];
                    const mapped = rows.map((r) => mapF4100RowToGrid(r));
                    setRowData(mapped);
                    setQuestionnaireData((prev) => ({
                      ...prev,
                      foundationalSetup: {
                        ...prev.foundationalSetup,
                        locations: mapped,
                      },
                    }));
                  } catch (e) {
                    console.error("Failed to load from jde.F4100:", e);
                  } finally {
                    setIsMappingOpen(false);
                  }
                }}
              />
            </div>
          </div>
        </div>
      )}
    </>
  );
};

export default Locations;

function buildTargetsFromColumns(columnDefs) {
  const cols = Array.isArray(columnDefs) ? columnDefs : [];
  return cols
    .filter((c) => !!c?.field)
    .map((c, idx) => ({
      // Use the field key as the target id so mappings align with grid fields
      id: c.field,
      label: c.headerName || c.field,
    }));
}

// Transform a recordset row from F4100 SELECT (aliased to grid target fields)
function mapF4100RowToGrid(r) {
  const genId = () =>
    `loc_${Math.random().toString(36).slice(2)}_${Date.now().toString(36)}`;

  const toArray = (v) =>
    v == null
      ? []
      : String(v)
          .split(/[,;|]/)
          .map((s) => s.trim())
          .filter(Boolean);

  const row = {
    ...defaultConfigItem,
    __id: r.__id || genId(),
    __status: undefined,
    // Map 1:1 for fields that were aliased in SQL
    locationName: r.locationName || r.Location_Name || "",
    locationId: r.locationId || r.Location_ID || r.LMLOCN || "",
    languageId: r.languageId || r.Locale || r.LMLA10 || "",
    timeZoneId: r.timeZoneId || r.Time_Zone_Reference_ID || "",
    countryId: r.countryId || r.Country || r.LMLA08 || "",
    regionName: r.regionName || r.Region || r.LMLA06 || "",
    subregion: r.subregion || r.Subregion || r.LMLA07 || r.LMAISL || "",
    address: r.address || "",
  };
  row.locationUsage = toArray(r.locationUsage || r.Location_Usage || r.LMLA03);
  row.locationType = toArray(r.locationType || r.Location_Type || r.LMLA05);
  row.commUsage = toArray(r.commUsage || r.LMLA04);
  return row;
}
